import fuzzysort from "fuzzysort";
import type { Place } from "@/hooks/usePlaces";
import { MAJOR_POI_TAG, PointsOfInterest, RM_TAGS } from "@/redux/apis/clientPoi/types";

export type LatLng = { lat: number; lng: number };

/** The favourite categories a saved client POI carries (Home/Work/Other). Recents/provider POIs don't —
 *  the same signal useAddressSearch and the booking Address step use to classify favourites. */
const FAVOURITE_TAGS: string[] = [RM_TAGS.Home, RM_TAGS.Work, RM_TAGS.Other];

/** True when a POI is one of the user's saved favourites, by its rm_tags. */
export const isFavouritePoi = (poi: PointsOfInterest): boolean => !!poi.rm_tags?.some((tag) => FAVOURITE_TAGS.includes(tag));

/**
 * How a chosen location reads in a booking / pin-location field. ANY POI carrying a label shows that name
 * alongside the real address — "Home (223 Liverpool St, Darlinghurst NSW)", "Sydney Airport (SYD) -
 * Terminal 3 (Mascot NSW 2020, AU)" — and one with no label shows the plain address.
 *
 * Not just saved favourites (DEV-1510). This used to require `isFavouritePoi`, which is true only for
 * Home/Work/Other, so an airport, hotel, ship terminal or major POI fell through to its bare address and a
 * pickup at Sydney Airport read "Mascot NSW 2020, AU". Mobile's `getLocationName` has never had that check
 * — it returns the label for any POI that has one — and this is the same rule.
 *
 * The PARENTHESISED form is web's, deliberately not mobile's bare label: an operator filling a booking
 * field needs the real address visible, and name-plus-address is already this app's design language.
 *
 * A label the address ALREADY OPENS WITH is dropped, because in that form it is not a name — it is the
 * first line of the address repeated. A real name is never a prefix of its own address — "Sydney Airport
 * (SYD) - Terminal 3" against "Mascot NSW 2020, AU" — so this costs a genuinely labelled POI nothing.
 *
 * KEPT after the POI factories stopped writing addresses into `rm_label`, because the factories were never
 * the only source. Since DEV-1508 every booking sends `rm_label` for each stop (`toBookingStop`), so every
 * Google-picked location booked through web while those factories were live persisted an address-derived
 * label server-side. Those rows come back as RECENTS from the POI API, and no client-side change reaches
 * them. Mobile needs no equivalent only because it renders the bare label, where a label equal to its own
 * address is invisible rather than doubled; the parenthesised form is what makes it visible here, so the
 * guard is the cost of web's format, not of the old factory defect.
 *
 * Selection always stores the POI's real address — this only changes the field's display text. Shared by
 * the booking Address step, the home "Book a trip" panel, and the Pin-a-location modal so they never drift.
 */
export const poiFieldLabel = (poi: PointsOfInterest): string => {
  const name = poi.rm_label?.trim();
  const address = poi.address ?? "";
  if (!name) return address;
  return address.toLowerCase().startsWith(name.toLowerCase()) ? address : `${name} (${address})`;
};

export const cleanQuery = (raw: string): string =>
  raw
    .replace(/[^a-zA-Z0-9]/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase()
    .trim();

/** Great-circle distance in kilometres. */
export const haversineDistance = (a: LatLng, b: LatLng): number => {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
};

/**
 * True when the POI may be booked given `allowedCountries` (lower-case ISO codes, see
 * useAddressSearchCountries); `null` means no restriction. A POI with no `country_code` — legacy favourites
 * saved before the code was captured, or a pin whose reverse-geocode failed — is kept: it cannot be judged,
 * and dropping it would hide long-standing favourites and the existing "Requires Validation" pin path
 * (DEV-1606, DEV-1612).
 */
export const isPoiInCountries = (poi: Pick<PointsOfInterest, "country_code">, allowedCountries: string[] | null): boolean =>
  allowedCountries === null || !poi.country_code || allowedCountries.some((code) => code.toLowerCase() === poi.country_code.toLowerCase());

/** The POIs that pass `isPoiInCountries` — applied to favourites, recents and provider POIs before they are offered. */
export const filterPoisByCountry = <T extends Pick<PointsOfInterest, "country_code">>(pois: T[], allowedCountries: string[] | null): T[] =>
  allowedCountries === null ? pois : pois.filter((poi) => isPoiInCountries(poi, allowedCountries));

/** Fuzzysort over saved/recent POIs with Major_POI + proximity boosts (mirrors mobile POIInput). */
export const rankLocalPois = (query: string, pois: PointsOfInterest[], userLocation?: LatLng): PointsOfInterest[] => {
  const cleaned = cleanQuery(query);
  if (!cleaned) return [];
  return fuzzysort
    .go(cleaned, pois, {
      keys: ["rm_label", "address"],
      threshold: 0.35,
      limit: 3,
      scoreFn: (result) => {
        const obj = result.obj;
        let score = result.score;
        if (obj.rm_tags?.includes(MAJOR_POI_TAG)) score += 0.05;
        if (userLocation && Number.isFinite(obj.lat) && Number.isFinite(obj.lng)) {
          const distance = haversineDistance(userLocation, { lat: obj.lat, lng: obj.lng });
          score += Math.exp(-0.01 * distance) * 0.5;
        }
        return score;
      },
    })
    .map((m) => m.obj);
};

/** De-duplicate local POIs out of the Google list and cap remote predictions. */
export const dedupeGooglePredictions = (predictions: Place[], exclusions: string[] = [], limit = 5): Place[] =>
  predictions.filter((p) => !exclusions.includes(p.place_id)).slice(0, limit);

export type AddressComponent = { long_name: string; short_name: string; types: string[] };

/** Address parts the backend requires on a POI, parsed from Google `address_components`. */
export type AddressParts = {
  city: string;
  postcode: string;
  state: string;
  state_code: string;
  country: string;
  country_code: string;
};

const EMPTY_ADDRESS_PARTS: AddressParts = {
  city: "",
  postcode: "",
  state: "",
  state_code: "",
  country: "",
  country_code: "",
};

/** Map Google `address_components` to the POI address fields (port of mobile formatAddressData). */
export const parseGoogleAddressComponents = (components: AddressComponent[] = []): AddressParts => {
  const find = (type: string) => components.find((c) => c.types?.includes(type));
  return {
    city: find("locality")?.long_name ?? "",
    postcode: find("postal_code")?.long_name ?? "",
    state: find("administrative_area_level_1")?.long_name ?? "",
    state_code: find("administrative_area_level_1")?.short_name ?? "",
    country: find("country")?.long_name ?? "",
    country_code: (find("country")?.short_name ?? "").toUpperCase(),
  };
};

/** Build a PointsOfInterest from a Google reverse-geocode result + coordinates (for a dragged pin). */
export const geocoderResultToPoi = (
  result: { place_id?: string; formatted_address?: string; address_components?: AddressComponent[] },
  coordinates: LatLng
): PointsOfInterest => {
  const address = result.formatted_address ?? "";
  return {
    uuid: "",
    vendor: "google",
    vendor_uuid: result.place_id ?? "",
    vendor_address: address,
    address,
    lat: coordinates.lat,
    lng: coordinates.lng,
    ...parseGoogleAddressComponents(result.address_components ?? []),
    // EMPTY, matching mobile (addressHelper.ts:246 — `rm_label: addressData.rm_label || ''`). A pinned or
    // geocoded location has no name: only a real POI from the POI API carries one. This used to copy the
    // whole formatted address into what is a NAME field, which then had to be undone at every display site.
    rm_label: "",
    rm_type: "",
    rm_tags: [],
    rm_exclusions: [],
  };
};

/** Build a PointsOfInterest from a resolved Google place + coordinates + parsed address parts. */
export const googlePlaceToPoi = (place: Place, coordinates: LatLng, parts: AddressParts = EMPTY_ADDRESS_PARTS): PointsOfInterest => ({
  uuid: "",
  vendor: "google",
  vendor_uuid: place.place_id,
  vendor_address: place.description,
  address: place.description,
  lat: coordinates.lat,
  lng: coordinates.lng,
  city: parts.city,
  postcode: parts.postcode,
  state: parts.state,
  state_code: parts.state_code,
  country: parts.country,
  country_code: parts.country_code,
  // EMPTY, as in `geocoderResultToPoi` and as on mobile. Google's `main_text` is the first line of the
  // address, not a name — storing it produced "Park Hyatt Sydney (Park Hyatt Sydney, The Rocks NSW)" in the
  // booking fields and, since DEV-1508, sent that duplicate to the server on every stop.
  rm_label: "",
  rm_type: "",
  rm_tags: [],
  rm_exclusions: [],
});
